// Defensive mappers from raw server payloads into agent view-models.
// Bad rows are dropped (returns null) so a single malformed agent does
// not blank out the catalog.

import type {
  AgentAccountType,
  AgentCatalogView,
  AgentDevStatus,
  AgentThreadSuggestion,
  AgentView,
  DeniedAgentView,
  ExamplePromptCategory,
} from './types';

const ACCOUNT_TYPES: ReadonlyArray<AgentAccountType> = ['single', 'cross', 'solution', 'orchestrator'];

const DEV_STATUSES: ReadonlyArray<AgentDevStatus> = [
  'live',
  'experimental',
  'development',
  'planned',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function toStringArray(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

function toOptionalStringArray(value: unknown): ReadonlyArray<string> | undefined {
  if (value === undefined || value === null) return undefined;
  return toStringArray(value);
}

function toExamplePromptCategories(value: unknown): ReadonlyArray<ExamplePromptCategory> | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const categories = value
    .filter(isRecord)
    .map((raw): ExamplePromptCategory | null => {
      const category = toOptionalString(raw.category);
      const prompts = toStringArray(raw.prompts);
      if (!category || prompts.length === 0) return null;
      return { category, prompts };
    })
    .filter((c): c is ExamplePromptCategory => c !== null);
  return categories.length > 0 ? categories : undefined;
}

function toAccountType(value: unknown): AgentAccountType | undefined {
  return typeof value === 'string' && ACCOUNT_TYPES.includes(value as AgentAccountType)
    ? (value as AgentAccountType)
    : undefined;
}

function toDevStatus(value: unknown): AgentDevStatus | undefined {
  return typeof value === 'string' && DEV_STATUSES.includes(value as AgentDevStatus)
    ? (value as AgentDevStatus)
    : undefined;
}

export function toAgentView(raw: unknown): AgentView | null {
  if (!isRecord(raw)) return null;

  const id = toOptionalString(raw.agent_id);
  if (!id) return null;

  const displayName = toOptionalString(raw.display_name) ?? toOptionalString(raw.name) ?? id;
  const description = toOptionalString(raw.description) ?? '';
  const version = toOptionalString(raw.version) ?? '';
  const runtime = toOptionalString(raw.runtime) ?? '';

  const view: AgentView = {
    id,
    displayName,
    description,
    version,
    runtime,
    tags: toStringArray(raw.tags),
    mcps: toStringArray(raw.mcps),
  };


  const accountType = toAccountType(raw.account_type);
  if (accountType !== undefined) view.accountType = accountType;

  const devStatus = toDevStatus(raw.dev_status);
  if (devStatus !== undefined) view.devStatus = devStatus;

  const bullets = toOptionalStringArray(raw.bullets);
  if (bullets !== undefined) view.bullets = bullets;

  const examplePrompts = toOptionalStringArray(raw.example_prompts);
  if (examplePrompts !== undefined) view.examplePrompts = examplePrompts;

  const examplePromptCategories = toExamplePromptCategories(raw.example_prompt_categories);
  if (examplePromptCategories !== undefined) view.examplePromptCategories = examplePromptCategories;

  return view;
}

/**
 * Flattens an agent's example prompts into the assistant-ui runtime's
 * `ThreadSuggestion` shape ({ title?, prompt }), in the same order
 * ChatEmptyStatePrompts renders them under category headers — that order is
 * what keeps its ThreadPrimitive.SuggestionByIndex indices in sync with the
 * runtime's registered suggestions.
 */
export function toAgentThreadSuggestions(agent: AgentView | undefined): AgentThreadSuggestion[] {
  if (!agent) return [];
  const prompts =
    agent.examplePromptCategories && agent.examplePromptCategories.length > 0
      ? agent.examplePromptCategories.flatMap(({ prompts: categoryPrompts }) => categoryPrompts)
      : (agent.examplePrompts ?? []);
  return prompts.map((prompt) => ({ title: prompt, prompt }));
}

export function toDeniedAgentView(raw: unknown): DeniedAgentView | null {
  if (!isRecord(raw)) return null;

  const id = toOptionalString(raw.agent_id);
  if (!id) return null;

  const view: DeniedAgentView = {
    id,
    displayName: toOptionalString(raw.display_name) ?? id,
    description: toOptionalString(raw.description) ?? '',
    requiredGroups: toStringArray(raw.required_groups),
  };


  const accountType = toAccountType(raw.account_type);
  if (accountType !== undefined) view.accountType = accountType;

  const devStatus = toDevStatus(raw.dev_status);
  if (devStatus !== undefined) view.devStatus = devStatus;

  const bullets = toOptionalStringArray(raw.bullets);
  if (bullets !== undefined) view.bullets = bullets;

  return view;
}

export function toAgentCatalogView(raw: unknown): AgentCatalogView {
  if (!isRecord(raw)) {
    return { agents: [], deniedAgents: [], isAdmin: false };
  }

  const agentsRaw = Array.isArray(raw.agents) ? raw.agents : [];
  const deniedRaw = Array.isArray(raw.denied_agents) ? raw.denied_agents : [];

  const agents = agentsRaw
    .map(toAgentView)
    .filter((a): a is AgentView => a !== null);
  const deniedAgents = deniedRaw
    .map(toDeniedAgentView)
    .filter((a): a is DeniedAgentView => a !== null);

  return {
    agents,
    deniedAgents,
    isAdmin: raw.is_admin === true,
  };
}
